import {
  CHECKIN,
  REGISTERED,
  SELF_CHECKIN,
  WAITING_LIST,
} from '../../containers/Register/enums';
import { CANCELLED, DIGITAL } from '../../enums';
import {
  AppState,
  CSSEvent,
  EventLoyaltyType,
  LoyaltyPrograms,
  Store,
  StoreMap,
  TimeSlot,
  User,
} from '../../types/model';
import sortEventsByStartDate from '../../utils/sortEventsByStartDate';

export const getStores = (state: AppState): StoreMap => state.stores;

export const getStoreList = (state: AppState): Array<Store> =>
  Object.values(state.stores);

export const getStoreBySlug =
  (slug: string) =>
    (state: AppState): Store | undefined =>
      slug
        ? Object.values(state.stores).find((s: Store) => s.slug === slug)
        : undefined;

export const getStoreById = (id: string) => (state: AppState) =>
  state.stores[id];

export const getSelectedStore = (state: AppState): Store =>
  state.stores[state.selectedStore || ''];

export const getPageError = (state: AppState) => state.pageError;

export const getEvents = (state: AppState): Array<CSSEvent> =>
  Object.values(state.events);

export const getStoreEvents =
  (includeDigital: boolean = true) =>
  (state: AppState): Array<CSSEvent> => {
    if (!state.selectedStore) {
      return [];
    }
    let ids = state.byStore[state.selectedStore] || [];
    if (includeDigital) {
      ids = [...ids, ...state.digitalEvents];
    }

    return [...new Set(ids)].map((eventId) => state.events[eventId]);
  };

export const getStoreEventsWithFilters =
  (filter: string[] = []) =>
  (state: AppState): Array<CSSEvent> => {
    if (!state.selectedStore) {
      return [];
    }

    const getKeyByValue = (value: string) => {
      const eventTypes = EventLoyaltyType as any;
      return (Object.keys(eventTypes) as (keyof typeof eventTypes)[]).find(
        (key) => eventTypes[key] === value
      );
    };

    let ids = state.byStore[state.selectedStore] || [];
    ids = [...ids, ...state.digitalEvents];
    let temp = [...new Set(ids)].map((eventId) => state.events[eventId]);

    return temp.filter((e) => {
      return (
        filter.includes(getKeyByValue(e.loyaltyEvent) as string) ||
        filter.includes(e.location.type)
      );
    });
  };

export const getSelectedEvent = (state: AppState): CSSEvent =>
  state.events[state.selectedEvent || ''];

export const getSelectedTimeslot = (
  state: AppState
): TimeSlot | null | undefined => {
  if (!state.selectedEvent || !state.events[state.selectedEvent]) {
    return null;
  }

  return state.events[state.selectedEvent].timeSlots.find(
    (t: TimeSlot) => t.timeslotId === state.selectedTimeslot
  );
};

export const getUser = (state: AppState): User | null => state.user;

export const isSignedIn = (state: AppState): boolean => Boolean(state.user);

export const isFamilyMember = (state: AppState): boolean => {
  const { user } = state;

  if (!user) {
    return false;
  }

  return user.loyalty === LoyaltyPrograms.ikeaFamily;
};

export const isUserRegisteredToEvent =
  (event: CSSEvent) => (state: AppState) => {
    if (!event) {
      return [false, null];
    }

    for (let timeSlot of event.timeSlots) {
      const registration = timeSlot.registration;
      if (
        registration &&
        [REGISTERED, WAITING_LIST, CHECKIN, SELF_CHECKIN].includes(
          registration.state
        )
      ) {
        return [true, registration.state];
      }
    }

    return [false, null];
  };

export const getUserWaitingListTimeslot = (state: AppState) => {
  const selectedEvent = getSelectedEvent(state);

  if (!selectedEvent) {
    return null;
  }

  const timeSlot = selectedEvent.timeSlots.find((t) => {
    const registration = t.registration;
    return registration?.state === WAITING_LIST;
  });

  return timeSlot?.timeslotId;
};

export function getRegisteredTimeSlotAndState(state: AppState) {
  const selectedEvent = getSelectedEvent(state);

  if (!selectedEvent) {
    return [null, null];
  }

  for (let timeSlot of selectedEvent.timeSlots) {
    const registration = timeSlot.registration;
    if (
      registration &&
      [REGISTERED, WAITING_LIST, CHECKIN, SELF_CHECKIN].includes(
        registration.state
      )
    ) {
      return [timeSlot.timeslotId, registration.state];
    }
  }
  return [null, null];
}

export function getAllRegisteredTimeSlots(state: AppState): Array<{ timeslotId: string; state: string }> {
  const selectedEvent = getSelectedEvent(state);

  if (!selectedEvent) {
    return [];
  }

  const registeredSlots: Array<{ timeslotId: string; state: string }> = [];
  for (const timeSlot of selectedEvent.timeSlots) {
    const registration = timeSlot.registration;
    if (
      registration &&
      [REGISTERED, WAITING_LIST, CHECKIN, SELF_CHECKIN].includes(
        registration.state
      )
    ) {
      registeredSlots.push({
        timeslotId: timeSlot.timeslotId,
        state: registration.state,
      });
    }
  }
  return registeredSlots;
}

export const getBreadcrumbs = (state: AppState) => state.breadcrumbs;

export const isDigitalEvent = (state: AppState) => {
  const event = getSelectedEvent(state);

  return Boolean(event?.location.type === DIGITAL);
};

export const getRandomUpcomingEvents = (state: AppState) =>
  getEvents(state)
    .filter((e) => e.visibilityState !== CANCELLED)
    .sort(() => 0.5 - Math.random())
    .sort(sortEventsByStartDate)
    .slice(0, 5);
